import { useCallback, useRef } from 'react';
import { useIdleTimer } from 'react-idle-timer';
import { useNavigate } from 'react-router-dom';

import caregiverService from '../../services/caregiverService';

const FIFTEEN_MINUTES = 15 * 60 * 1000;
// Re-mint the caregiver token at most this often while active. Comfortably
// shorter than the backend token ceiling (CAREGIVER_SESSION_TOKEN_MINUTES, 30m)
// so an active session is always renewed well before its token expires, while
// staying far above per-keystroke cadence so activity bursts cause one call.
const RENEW_THROTTLE_MS = 5 * 60 * 1000;

/**
 * Caregiver inactivity timeout + sliding-session renewal (FECOA-1514).
 *
 * - AC1: logs the caregiver out after 15 minutes with no input or navigation.
 * - AC2: any activity (key, click, touch, scroll) resets the idle timer AND,
 *   at most once per RENEW_THROTTLE_MS, re-mints the caregiver token via
 *   `extendSession`. Caregiver tokens have no refresh, so without this an
 *   active caregiver would still be 401'd mid-survey when the per-token ceiling
 *   elapsed. Renewal stops the instant the caregiver goes idle.
 * - AC5: on idle logout the caregiver is sent to /caregiver-login?expired=1,
 *   where a "session expired" banner is shown.
 *
 * Rendered only inside CaregiverSessionGuard, so it runs exclusively for an
 * authenticated caregiver session. This is intentionally separate from the
 * staff/participant IdleTimer, which is coupled to AuthContext (caregivers do
 * not use AuthContext) and is disabled in local development.
 */
const CaregiverIdleTimer = () => {
  const navigate = useNavigate();
  // Seed with the mount time: the token is fresh from login/navigation, so the
  // first renewal is throttled until RENEW_THROTTLE_MS of activity has elapsed.
  const lastRenewRef = useRef(Date.now());

  const handleIdle = useCallback(() => {
    caregiverService.logout();
    navigate('/caregiver-login?expired=1', { replace: true });
  }, [navigate]);

  const handleAction = useCallback(() => {
    const now = Date.now();
    if (now - lastRenewRef.current < RENEW_THROTTLE_MS) return;
    lastRenewRef.current = now;
    // Failures (e.g. an already-lapsed token → 401) are handled by the api
    // interceptor, which redirects to the expired-login page.
    caregiverService.extendSession().catch(() => {});
  }, []);

  // Per-tab timer (no crossTab): caregiver sessions live in sessionStorage, so
  // each tab is its own independent session.
  useIdleTimer({
    timeout: FIFTEEN_MINUTES,
    onIdle: handleIdle,
    onAction: handleAction,
    debounce: 500,
  });

  return null;
};

export default CaregiverIdleTimer;
